/**
 * Collapsible tab deep-linking.
 *
 * Lets a URL like /products/foo#Specifications open the matching product
 * accordion tab and scroll it into view, clear of the sticky header and the
 * sticky add-to-cart bar.
 *
 * The anchor id lives on the `.product__accordion` wrapper (see
 * sections/main-product.liquid). The id is the tab heading with spaces removed
 * and case preserved, e.g. "Specifications" -> #Specifications.
 */
(function () {
  'use strict';

  /**
   * Calculate the final Y scroll position (in px from the top of the document)
   * for a freshly-opened tab.
   *
   * The product page pins two things to the top of the viewport — the sticky
   * header and the sticky ATC bar — so scrolling to the tab's raw position
   * would land it behind them. We subtract both heights (the ATC bar only when
   * it's actually visible) plus a small gap so the summary sits just below them.
   *
   * @param {HTMLElement} target - the .product__accordion wrapper being opened
   * @returns {number} absolute Y position to scroll the window to
   */
  function getScrollOffset(target) {
    var GAP = 50; // breathing room below the sticky bars

    var targetTop = target.getBoundingClientRect().top + window.pageYOffset;

    var header = document.querySelector('sticky-header, .header-wrapper');
    var headerHeight = header ? header.offsetHeight : 0;

    // Only the *visible* ATC bar occupies space — it carries `.is-visible`
    // once it appears. On first paint it's usually hidden, so it adds 0.
    var atcBar = document.querySelector('.sticky-atc-bar.is-visible');
    var atcBarHeight = atcBar ? atcBar.offsetHeight : 0;

    return Math.max(0, targetTop - headerHeight - atcBarHeight - GAP);
  }

  function smoothScrollTo(endY) {
    const startY = window.pageYOffset;
    const distance = endY - startY;
    const duration = 600;
    let start = null;

    function step(now) {
      if (start === null) start = now;
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // easeInOutQuad — matches component-blocks-tabs.js
      const ease = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      window.scrollTo(0, startY + distance * ease);

      if (elapsed < duration) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }

  function openTabFromHash() {
    if (!window.location.hash) return;

    // Decode (e.g. %20) and lowercase so any casing in the URL resolves to the
    // lowercase id we emit, e.g. #Specifications and #specifications both match.
    const rawId = decodeURIComponent(window.location.hash.slice(1)).toLowerCase();
    if (!rawId) return;

    const wrapper = document.getElementById(rawId);
    // Only act on collapsible product tabs — ignore unrelated ids on the page.
    if (!wrapper || !wrapper.classList.contains('product__accordion')) return;

    const details = wrapper.querySelector('details');
    if (details) details.open = true;

    smoothScrollTo(getScrollOffset(wrapper));
  }

  // Run after layout settles so sticky bar heights are accurate.
  window.addEventListener('load', function () {
    setTimeout(openTabFromHash, 100);
  });
})();
